import {
  EVENT_VISIBILITYCHANGE,
  EVENT_CLICK,
  EVENT_TOUCHSTART,
  EVENT_KEYDOWN,
  EVENT_RELEASE,
} from './strings';

export interface ScreenWakeLockOptions {
  /**
   * Inactivity timeout in ms after which the wake lock will be released if pokeActivity() is not called.
   * If undefined or <= 0, the lock remains held until explicitly released or destroyed.
   */
  inactivityTimeoutMs?: number;
}

/**
 * ScreenWakeLock manages the W3C Screen Wake Lock API.
 * Keeps the screen awake during live presentations and live broadcast viewing.
 */
export class ScreenWakeLock {
  private sentinel: WakeLockSentinel | null = null;
  private inactivityTimeoutMs: number;
  private timeoutId: number | null = null;
  private isRequested: boolean = false;
  private isAcquiring: boolean = false;
  private isDestroyed: boolean = false;

  constructor(options?: number | ScreenWakeLockOptions) {
    if (typeof options === 'number') {
      this.inactivityTimeoutMs = options;
    } else {
      this.inactivityTimeoutMs = options?.inactivityTimeoutMs ?? 0;
    }

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleUserInteraction = this.handleUserInteraction.bind(this);

    document.addEventListener(EVENT_VISIBILITYCHANGE, this.handleVisibilityChange);
    window.addEventListener(EVENT_CLICK, this.handleUserInteraction, { passive: true });
    window.addEventListener(EVENT_TOUCHSTART, this.handleUserInteraction, { passive: true });
    window.addEventListener(EVENT_KEYDOWN, this.handleUserInteraction, { passive: true });
  }

  /**
   * Request that the screen be kept awake.
   */
  requestLock(): void {
    if (this.isDestroyed) return;
    this.isRequested = true;
    this.resetTimer();
    this.acquire();
  }

  /**
   * Called when active content or user interaction occurs.
   * Ensures the lock is acquired if desired and resets the inactivity timer.
   */
  pokeActivity(): void {
    if (this.isDestroyed) return;
    this.isRequested = true;
    this.resetTimer();
    if (!this.sentinel && !this.isAcquiring) {
      this.acquire();
    }
  }

  private resetTimer(): void {
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.inactivityTimeoutMs > 0) {
      this.timeoutId = window.setTimeout(() => {
        this.releaseLock();
      }, this.inactivityTimeoutMs);
    }
  }

  private async acquire(): Promise<void> {
    if (this.isDestroyed || !this.isRequested) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    if (document.visibilityState !== 'visible') return;
    if (this.sentinel || this.isAcquiring) return;

    try {
      this.isAcquiring = true;
      const sentinel = await navigator.wakeLock.request('screen');
      // If releaseLock() or destroy() was called while awaiting:
      if (!this.isRequested || this.isDestroyed) {
        await sentinel.release().catch(() => {});
        return;
      }
      this.sentinel = sentinel;
      this.sentinel.addEventListener(EVENT_RELEASE, () => {
        if (this.sentinel === sentinel) {
          this.sentinel = null;
        }
      });
    } catch {
      // Browser may reject if window is not active or due to power-saving settings.
      // User interaction listener will retry when user clicks or touches.
    } finally {
      this.isAcquiring = false;
    }
  }

  /**
   * Releases any currently held wake lock.
   */
  releaseLock(): void {
    this.isRequested = false;
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.sentinel) {
      this.sentinel.release().catch(() => {});
      this.sentinel = null;
    }
  }


  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible' && this.isRequested) {
      this.acquire();
    }
  }

  private handleUserInteraction(): void {
    if (this.isRequested && !this.sentinel) {
      this.acquire();
    }
  }

  destroy(): void {
    this.isDestroyed = true;
    this.releaseLock();
    document.removeEventListener(EVENT_VISIBILITYCHANGE, this.handleVisibilityChange);
    window.removeEventListener(EVENT_CLICK, this.handleUserInteraction);
    window.removeEventListener(EVENT_TOUCHSTART, this.handleUserInteraction);
    window.removeEventListener(EVENT_KEYDOWN, this.handleUserInteraction);
  }
}
